import {useCallback, useMemo, useState} from 'react'
import {
  usePatchSession,
  PLAN_STATE_APPROVED,
  PLAN_STATE_DISMISSED,
  PLAN_EVENT_APPROVED,
  PLAN_EVENT_DISMISSED,
  PLAN_STATE_TO_EVENT,
  type PlanState,
} from './use-patch-session'
import type {Session} from '../types/session'

interface UsePendingPlanGateOptions {
  sessionsData: Session[] | undefined
  lastSessionId: string | undefined
}

/**
 * Manages plan approval state and the pending-plan gate that hides the chat input
 * while a plan is awaiting user action.
 *
 * Extracts plan state derivation, optimistic updates, and the hasPendingPlan flag
 * from TaskChat to keep that file focused on rendering.
 */
export function usePendingPlanGate({sessionsData, lastSessionId}: UsePendingPlanGateOptions) {
  const [optimisticPlanStates, setOptimisticPlanStates] = useState<Record<string, PlanState>>({})
  const [pendingPlanContent, setPendingPlanContent] = useState<string | null>(null)

  const planStates = useMemo(() => {
    const states: Record<string, PlanState> = {}
    const confirmedIds = new Set<string>()
    if (sessionsData) {
      for (const session of sessionsData) {
        if (session.event_identifiers?.includes(PLAN_EVENT_APPROVED)) {
          states[session.id] = PLAN_STATE_APPROVED
          confirmedIds.add(session.id)
        } else if (session.event_identifiers?.includes(PLAN_EVENT_DISMISSED)) {
          states[session.id] = PLAN_STATE_DISMISSED
          confirmedIds.add(session.id)
        }
      }
    }
    // Layer in optimistic entries that the server hasn't confirmed yet
    for (const [sessionId, state] of Object.entries(optimisticPlanStates)) {
      if (!confirmedIds.has(sessionId)) {
        states[sessionId] = state
      }
    }
    return states
  }, [sessionsData, optimisticPlanStates])

  // pendingPlanContent is non-null when the child reported plan content.
  // hasPendingPlan auto-clears when the last session's plan is resolved
  // (either locally via optimistic update or externally via polling).
  const hasPendingPlan = pendingPlanContent !== null && !(lastSessionId && planStates[lastSessionId])

  const {mutate: patchSession} = usePatchSession({
    onError: (_error, variables) => {
      setOptimisticPlanStates(prev => {
        const next = {...prev}
        delete next[variables.session_id]
        return next
      })
    },
  })

  const updatePlanState = useCallback(
    (sessionId: string, state: PlanState) => {
      const freshSession = sessionsData?.find(s => s.id === sessionId)
      if (!freshSession) return
      const newEventIdentifiers = [...(freshSession.event_identifiers ?? []), PLAN_STATE_TO_EVENT[state]]
      setOptimisticPlanStates(prev => ({...prev, [sessionId]: state}))
      setPendingPlanContent(null)
      patchSession({
        user_id: freshSession.user_id,
        session_id: freshSession.id,
        task_id: freshSession.task_id,
        event_identifiers: newEventIdentifiers,
      })
    },
    [sessionsData, patchSession],
  )

  return {
    planStates,
    hasPendingPlan,
    pendingPlanContent: hasPendingPlan ? pendingPlanContent : null,
    setPendingPlanContent,
    updatePlanState,
  }
}
